import { useState } from "react"
import isEmpty from "lodash.isempty"

import { wrapArray } from "web/services/helpers"
import { Field, Form } from "web/components"

import MobileHeader from "./MobileHeader"
import { DropDownActionButtons } from "../Buttons"
import OperatorRadio from "../OperatorRadio"
import {
  conditionFromFormValue,
  getOperators,
  valueIsMultiSelectable,
  valueIsNumerical,
} from "./helpers"

const defaultMinHeight = "md:min-h-none min-h-full"

const fieldKindMinHeight = {
  Date: "md:min-h-lg min-h-full",
  Lookup: "md:min-h-sm min-h-full",
  Text: "md:min-h-sm min-h-full",
  SortProxy: "md:min-h-sm min-h-full",
}

export function getDefaultValues(fieldKind, condition) {
  const operator = condition.operator
    ? getOperators(fieldKind).find((op) => op.value === condition.operator)
    : { value: "equal", label: "equal" }

  const value = valueIsMultiSelectable(fieldKind, operator.value)
    ? wrapArray(condition.value) || []
    : condition.value || {}

  return {
    match_blank: condition.match_blank
      ? { value: condition.match_blank }
      : { value: false },
    operator,
    ...(valueIsNumerical(fieldKind) &&
      Array.isArray(value) && { minValue: value[0], maxValue: value[1] }),
    ...((!valueIsNumerical(fieldKind) || !Array.isArray(value)) && {
      value,
    }),
  }
}

export default function EditConditionDropDown({
  field,
  condition,
  onCancel = () => {},
  onChange = () => {},
  onClear = () => {},
}) {
  const fieldKind = field.sort_proxy_filtering_enabled
    ? "SortProxy"
    : field.kind

  const [formValue, setFormValue] = useState(
    getDefaultValues(fieldKind, condition)
  )
  const hasMultiOperators = getOperators(fieldKind).length > 1
  const isRange = formValue.operator.value === "range"

  function isValidRange() {
    const { minValue, maxValue } = formValue
    if (!isNaN(minValue?.value) && !isNaN(maxValue?.value)) {
      return (
        parseFloat(minValue.value) <= parseFloat(maxValue.value) &&
        minValue.unit === maxValue.unit
      )
    }
    return false
  }

  // Filter condition builder needs to allow selecting any of the lookup values,
  // irrespective of whether the 'Show If' condition matches.
  const allLookupValues = field.lookup_values.map((v) => ({
    ...v,
    show_expression: null,
  }))
  const minHeight = fieldKindMinHeight[fieldKind]
    ? fieldKindMinHeight[fieldKind]
    : defaultMinHeight

  const formFields = [
    {
      ...field,
      name: "value",
      key: "value",
      label: null,
      kind: fieldKind,
      is_attribute: false,
      expression: null,
      show_expression: null,
      lookup_values: allLookupValues,
      canShowAddXOption: false,
      multiple_values: valueIsMultiSelectable(
        fieldKind,
        formValue.operator.value
      ),
    },
    {
      key: "operator",
      name: "operator",
      kind: "Lookup",
      lookup_values: getOperators(fieldKind),
      label: "Comparison",
      format: "Radio",
    },
    {
      key: "match_blank",
      name: "match_blank",
      kind: "Boolean",
      label: `Include records without ${field.label}`,
    },
  ]

  const isEmptyOrNotEmptyOperator = ["empty", "not_empty"].includes(
    formValue.operator.value
  )

  const validFormInput = !isRange || isValidRange()

  return (
    <div
      className={`flex max-w-full flex-col p-6 md:min-w-sm md:max-w-md ${minHeight}`}
    >
      <MobileHeader field={field} onCancel={onCancel} />
      <Form
        value={formValue}
        onChange={(newValue) => {
          setFormValue(newValue)
        }}
        onSubmit={() => {
          if (!validFormInput) return false
          onChange(conditionFromFormValue(field, formValue))
        }}
        fields={formFields}
        render={(form) => (
          <>
            <div className="flex flex-grow flex-col space-y-6 md:flex-row md:justify-between md:space-y-0">
              {hasMultiOperators && (
                <div>
                  <Field
                    fieldKey="operator"
                    showValueLabel={false}
                    flexDirection="col"
                    Control={OperatorRadio}
                  />
                </div>
              )}
              {!isEmptyOrNotEmptyOperator && (
                <div className="max-h-96 w-52 overflow-y-auto px-0.5">
                  {!valueIsNumerical(fieldKind) && (
                    <Field
                      fieldKey="value"
                      autoFocus={isEmpty(formValue.value)}
                      allowQuickAdd={fieldKind == "Text"}
                      onChange={(newValue) =>
                        setFormValue((formValue) => ({
                          ...formValue,
                          value: newValue,
                        }))
                      }
                    />
                  )}
                  {valueIsNumerical(fieldKind) &&
                    (isRange ? (
                      <>
                        <Field
                          label="Minimum"
                          fieldKey="value"
                          value={formValue.minValue}
                          onChange={(newValue) =>
                            setFormValue((formValue) => ({
                              ...formValue,
                              minValue: newValue,
                            }))
                          }
                        />
                        <Field
                          label="Maximum"
                          fieldKey="value"
                          value={formValue.maxValue}
                          onChange={(newValue) =>
                            setFormValue((formValue) => ({
                              ...formValue,
                              maxValue: newValue,
                            }))
                          }
                        />
                      </>
                    ) : (
                      <Field
                        fieldKey="value"
                        autoFocus={isEmpty(formValue.value)}
                        onChange={(newValue) =>
                          setFormValue((formValue) => ({
                            ...formValue,
                            value: newValue,
                          }))
                        }
                      />
                    ))}
                </div>
              )}
            </div>
            <div className="justify-end pt-4">
              {!isEmptyOrNotEmptyOperator &&
                !field.sort_proxy_filtering_enabled && (
                  <div className="flex justify-end pb-4 text-sm">
                    <Field
                      fieldKey="match_blank"
                      inline={true}
                      showValueLabel={false}
                    />
                  </div>
                )}
              <div className="flex items-center">
                <DropDownActionButtons
                  handleClear={onClear}
                  handleAdd={form.submit}
                  addDisabled={!validFormInput}
                />
              </div>
            </div>
          </>
        )}
      ></Form>
    </div>
  )
}
